import React from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { Newspaper, Exam, Article, ArrowRight, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

const SECTIONS = [
    {
        id: "articles",
        title: "Daily Articles",
        desc: "UPSC-focused snippets curated daily from The Hindu & The New Indian Express — India & Karnataka-connected pieces mapped to GS papers.",
        Icon: Newspaper,
        tint: "from-emerald-50 to-white",
        accent: "text-emerald-700",
    },
    {
        id: "mcqs",
        title: "Daily MCQ's",
        desc: "10 fresh MCQs generated each day from The Hindu & The New Indian Express articles.",
        Icon: Exam,
        tint: "from-amber-50 to-white",
        accent: "text-amber-700",
    },
    {
        id: "editorials",
        title: "Daily Editorials",
        desc: "AI-analysed editorial breakdowns from today's The Hindu & New Indian Express — with GS relevance, arguments, stakeholder views, mains-question drills & answer-writing frameworks.",
        Icon: Article,
        tint: "from-indigo-50 to-white",
        accent: "text-indigo-700",
    },
];

export default function CurrentAffairs() {
    const navigate = useNavigate();
    const [starting, setStarting] = React.useState(false);

    const startDailyMcqs = async () => {
        if (starting) return;
        setStarting(true);
        try {
            const { data } = await api.post("/current-affairs/daily-session");
            toast.success(`Loaded ${data.questions.length} questions from ${data.date}`);
            navigate(`/mcq/test/${data.session_id}`, { state: data });
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not start today's MCQs");
        } finally {
            setStarting(false);
        }
    };

    const handleClick = (id) => {
        if (id === "articles") navigate("/current-affairs/articles");
        else if (id === "editorials") navigate("/current-affairs/editorials");
        else if (id === "mcqs") startDailyMcqs();
    };

    return (
        <Layout>
            <div className="mb-8 text-center">
                <div className="inline-flex items-center gap-2 rounded-full border-2 border-emerald-200 bg-emerald-50/70 px-3 py-1 text-xs font-semibold text-emerald-800 mb-3">
                    <Sparkle size={14} weight="fill" /> Updated daily
                </div>
                <h1 className="font-heading font-black text-3xl sm:text-4xl">Current Affairs</h1>
                <p className="text-slate-500 mt-2 text-sm">A three-pronged daily routine — read, practice, analyse.</p>
            </div>

            <div className="grid gap-5 md:grid-cols-3 max-w-5xl mx-auto">
                {SECTIONS.map((s) => (
                    <button
                        key={s.id}
                        data-testid={`ca-${s.id}`}
                        onClick={() => handleClick(s.id)}
                        disabled={s.id === "mcqs" && starting}
                        className={`text-left bento-card bg-gradient-to-br ${s.tint} group disabled:opacity-60`}
                    >
                        <div className="flex items-center gap-2 mb-3">
                            <div className={`p-2 rounded-lg bg-white/70 ${s.accent}`}>
                                <s.Icon size={22} weight="duotone" />
                            </div>
                            <div className={`text-[10px] uppercase tracking-[0.25em] font-semibold ${s.accent}`}>Today</div>
                        </div>
                        <div className="font-heading font-bold text-xl text-slate-900 leading-tight">{s.title}</div>
                        <div className="text-sm text-slate-600 mt-3 leading-relaxed">{s.desc}</div>
                        <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#1E3A8A] group-hover:gap-3 transition-all">
                            {s.id === "mcqs" ? (starting ? "Preparing…" : "Start today's MCQs") : "Read"}
                            <ArrowRight size={16} weight="bold" />
                        </div>
                    </button>
                ))}
            </div>
        </Layout>
    );
}
